import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'

const SELECTORS = {
	hoverImage: '[data-uc-mega-menu-hover-image]',
	imageLink: '[data-uc-mega-menu-image]',
} as const

export class MegaMenuHoverMedia extends UcoastEl {
	static htmlSelector = 'mega-menu-hover-media'

	hoverImage?: HTMLImageElement
	hoveredLink?: HTMLAnchorElement
	focusedLink?: HTMLAnchorElement

	constructor() {
		super()
		this.handleImageEnter = this.handleImageEnter.bind(this)
		this.handleImageLeave = this.handleImageLeave.bind(this)
		this.handleImageLoad = this.handleImageLoad.bind(this)
	}

	override connectedCallback() {
		super.connectedCallback()
		this.hoverImage = q.os<HTMLImageElement>(SELECTORS.hoverImage, this)
		if (!this.hoverImage) return

		this.addEventListener('mouseover', this.handleImageEnter)
		this.addEventListener('focusin', this.handleImageEnter)
		this.addEventListener('mouseout', this.handleImageLeave)
		this.addEventListener('focusout', this.handleImageLeave)
		this.hoverImage.addEventListener('load', this.handleImageLoad)
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.removeEventListener('mouseover', this.handleImageEnter)
		this.removeEventListener('focusin', this.handleImageEnter)
		this.removeEventListener('mouseout', this.handleImageLeave)
		this.removeEventListener('focusout', this.handleImageLeave)
		this.hoverImage?.removeEventListener('load', this.handleImageLoad)
	}

	handleImageEnter(event: Event) {
		const link = q.oClosestTarget<Event, HTMLAnchorElement>(event, SELECTORS.imageLink)
		if (!link || !this.hoverImage) return
		const relatedTarget = event instanceof MouseEvent ? event.relatedTarget : null
		if (relatedTarget instanceof Node && link.contains(relatedTarget)) return

		if (event.type === 'mouseover') {
			this.hoveredLink = link
		} else {
			this.focusedLink = link
		}

		this.showImage(link)
	}

	showImage(link: HTMLAnchorElement) {
		if (!this.hoverImage) return

		const image = link.dataset.ucMegaMenuImage
		if (!image) return

		this.hoverImage.removeAttribute('data-active')
		this.hoverImage.dataset.requestedImage = image
		this.hoverImage.src = image
		this.hoverImage.style.objectPosition =
			link.dataset.ucMegaMenuImagePosition || 'center'

		if (this.hoverImage.complete && this.hoverImage.naturalWidth > 0) {
			this.hoverImage.setAttribute('data-active', '')
		}
	}

	handleImageLeave(event: Event) {
		const link = q.oClosestTarget<Event, HTMLAnchorElement>(event, SELECTORS.imageLink)
		if (!link || !this.hoverImage) return

		const relatedTarget =
			event instanceof MouseEvent || event instanceof FocusEvent
				? event.relatedTarget
				: null
		const nextLink =
			relatedTarget instanceof Element
				? relatedTarget.closest<HTMLAnchorElement>(SELECTORS.imageLink)
				: null
		if (nextLink === link) return

		if (event.type === 'mouseout') {
			this.hoveredLink = nextLink && this.contains(nextLink) ? nextLink : undefined
		} else {
			this.focusedLink = nextLink && this.contains(nextLink) ? nextLink : undefined
		}

		const activeLink = this.hoveredLink || this.focusedLink
		if (activeLink) {
			this.showImage(activeLink)
			return
		}

		delete this.hoverImage.dataset.requestedImage
		this.hoverImage.removeAttribute('data-active')
	}

	handleImageLoad() {
		if (this.hoverImage?.dataset.requestedImage) {
			this.hoverImage.setAttribute('data-active', '')
		}
	}
}
